import { CredentialFileRendition } from "@api/shared-runtime/enums";
import {
  fetchCredentialFile,
  getCredentialFileUrls,
} from "@/ajax/documents/credential-file-urls";
import { generateDocumentName } from "@/features/documents/utils/generateDocumentName";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { FileOpener } from "@capacitor-community/file-opener";
import { Capacitor } from "@capacitor/core";
import { FileType } from "@/features/files/types/file-type";

type ZipModule = typeof import("@zip.js/zip.js");

let zipModulePromise: Promise<ZipModule> | null = null;

const loadZipModule = () => {
  if (!zipModulePromise) {
    zipModulePromise = import("@zip.js/zip.js");
  }
  return zipModulePromise;
};

export async function downloadAllFilesZip({
  userId,
  userName,
  files,
  organizationBusinessUnitId,
  batchSize = 10,
}: {
  /** The documents' owner. */
  userId: string;
  userName: string;
  files: FileType[];
  organizationBusinessUnitId?: string;
  batchSize?: number;
}) {
  const { ZipWriter, BlobWriter, Uint8ArrayReader } = await loadZipModule();
  const zipFileWriter = new BlobWriter("application/zip");
  const zipWriter = new ZipWriter(zipFileWriter);

  try {
    // Group files by type_name
    const filesByType: { [key: string]: FileType[] } = {};
    files.forEach((file) => {
      const typeName = file.type_name || "Other";
      if (!filesByType[typeName]) {
        filesByType[typeName] = [];
      }
      filesByType[typeName].push(file);
    });

    // Batch across type folders, so each signing call covers a full batch
    // rather than one call per type. Order stays grouped by type.
    const orderedFiles = Object.entries(filesByType).flatMap(
      ([typeName, typeFiles]) => typeFiles.map((file) => ({ typeName, file })),
    );

    // Track filenames to handle duplicates; keys include the type folder.
    const fileNameCounts: Record<string, number> = {};
    const fileNameIndices: Record<string, number> = {};

    for (let i = 0; i < orderedFiles.length; i += batchSize) {
      const batch = orderedFiles.slice(i, i + batchSize);

      // Sign each batch just before fetching it, so links for a long
      // export do not expire while earlier batches download.
      const urls = await getCredentialFileUrls({
        switchUserId: userId,
        organizationBusinessUnitId,
        versions: batch.map(({ file }) => ({
          versionId: file.id,
          rendition: CredentialFileRendition.Original,
        })),
      });

      // Download files in parallel
      const downloadPromises = batch.map(async ({ typeName, file }, index) => {
        const data = await fetchCredentialFile(urls[index]);
        const fileName = generateDocumentName(file);
        return { name: fileName, data, typeName, originalFile: file };
      });

      // Wait for all downloads in the current batch to complete
      const downloadedFiles = await Promise.all(downloadPromises);

      // First pass: count occurrences of each filename
      for (const file of downloadedFiles) {
        // Escape slashes in filenames
        const escapedFileName = file.name.replace(/\//g, "_");
        const escapedTypeName = file.typeName.replace(/\//g, "_");
        const typeAndNameKey = `${escapedTypeName}/${escapedFileName}`;
        fileNameCounts[typeAndNameKey] =
          (fileNameCounts[typeAndNameKey] || 0) + 1;
      }

      // Add downloaded files to the zip in their type folders
      for (const file of downloadedFiles) {
        // Escape slashes in filenames
        const escapedFileName = file.name.replace(/\//g, "_");
        const escapedTypeName = file.typeName.replace(/\//g, "_");
        const typeAndNameKey = `${escapedTypeName}/${escapedFileName}`;
        fileNameIndices[typeAndNameKey] =
          (fileNameIndices[typeAndNameKey] || 0) + 1;

        // Only add index if there are multiple files with the same name
        let uniqueFileName = escapedFileName;
        if (fileNameCounts[typeAndNameKey] > 1) {
          const nameParts = escapedFileName.split(".");
          if (nameParts.length > 1) {
            // Insert index before file extension
            const extension = nameParts.pop();
            uniqueFileName = `${nameParts.join(".")}_${fileNameIndices[typeAndNameKey]}.${extension}`;
          } else {
            // No extension, just append index
            uniqueFileName = `${escapedFileName}_${fileNameIndices[typeAndNameKey]}`;
          }
        }

        const folderPath = `${escapedTypeName}/${uniqueFileName}`;
        await zipWriter.add(
          folderPath,
          new Uint8ArrayReader(new Uint8Array(await file.data.arrayBuffer())),
        );
      }
    }

    // Close the zip writer and get the zip file as a blob
    const zipBlob = await zipWriter.close();
    const zipFileName = `${userName}_Saile_Credentials_Documents.zip`;

    if (Capacitor.isNativePlatform()) {
      // Convert Blob to base64 string
      const base64Data = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(zipBlob);
        reader.onloadend = () => {
          const base64String = reader.result as string;
          resolve(base64String.split(",")[1]);
        };
      });

      await Filesystem.requestPermissions();

      // Write to Documents directory for iOS
      const result = await Filesystem.writeFile({
        path: zipFileName,
        data: base64Data,
        directory: Directory.Documents,
        recursive: true,
      });

      await FileOpener.open({
        filePath: result.uri,
        openWithDefault: true,
      });
    } else {
      // Web download
      const downloadLink = document.createElement("a");
      downloadLink.href = URL.createObjectURL(zipBlob);
      downloadLink.download = zipFileName;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  } catch (error) {
    console.error("Error creating zip file:", error);
  }
}
